/**
 * 공통 유효성 검증 유틸리티
 * 폼 필드 검증 함수 및 에러 메시지 관리
 */

import {
    EMAIL_REGEX,
    PHONE_REGEX,
    PASSWORD_REGEX,
} from '@/shared/constants/validationPatterns.constants';

// ============================================================
// 에러 메시지 상수
// ============================================================

/** 비밀번호 에러 메시지 */
export const PASSWORD_ERROR_MESSAGE = '비밀번호는 8자 이상이며 영문, 숫자, 특수문자를 포함해야 합니다';

/** 휴대폰번호 에러 메시지 */
export const PHONE_ERROR_MESSAGE = '휴대폰번호는 10~12자리 숫자만 입력해주세요';

/** 이메일 에러 메시지 */
export const EMAIL_ERROR_MESSAGE = '올바른 이메일 형식이 아닙니다';

// ============================================================
// 타입 정의
// ============================================================

/** 유효성 검증 에러 객체 타입 */
export interface ValidationErrors {
    [key: string]: string;
}

/** 필드 검증자 타입 */
export interface FieldValidator {
    name: string;
    validator: () => string;
}

// ============================================================
// 개별 필드 검증 함수
// ============================================================

/**
 * 필수 필드 검증
 * @param value - 검증할 값
 * @param fieldName - 필드명 (에러 메시지용)
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validateRequired = (value: string, fieldName: string): string => {
    if (!value || value.trim().length === 0) {
        return `${fieldName}을(를) 입력해주세요.`;
    }
    return '';
};

/**
 * 최소 길이 검증
 * @param value - 검증할 값
 * @param minLength - 최소 길이
 * @param fieldName - 필드명 (에러 메시지용)
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validateMinLength = (value: string, minLength: number, fieldName: string): string => {
    if (value.length < minLength) {
        return `${fieldName}은(는) ${minLength}자 이상 입력해주세요.`;
    }
    return '';
};

/**
 * 아이디 유효성 검증
 * @param userId - 검증할 아이디
 * @param minLength - 최소 길이 (기본값: 4)
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validateUserId = (userId: string, minLength = 4): string => {
    const requiredError = validateRequired(userId, '아이디');
    if (requiredError) return requiredError;

    const minLengthError = validateMinLength(userId, minLength, '아이디');
    if (minLengthError) return minLengthError;

    return '';
};

/**
 * 사용자 이름 유효성 검증
 * @param userName - 검증할 사용자 이름
 * @param minLength - 최소 길이 (기본값: 2)
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validateUserName = (userName: string, minLength = 2): string => {
    const requiredError = validateRequired(userName, '이름');
    if (requiredError) return requiredError;

    const minLengthError = validateMinLength(userName, minLength, '이름');
    if (minLengthError) return minLengthError;

    return '';
};

/**
 * 비밀번호 유효성 검증
 * @param password - 검증할 비밀번호
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validatePassword = (password: string): string => {
    const requiredError = validateRequired(password, '비밀번호');
    if (requiredError) return requiredError;

    if (!PASSWORD_REGEX.test(password)) {
        return PASSWORD_ERROR_MESSAGE;
    }
    return '';
};

/**
 * 비밀번호 확인 검증
 * @param password - 원본 비밀번호
 * @param confirmPassword - 확인용 비밀번호
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validatePasswordConfirmation = (
    password: string,
    confirmPassword: string
): string => {
    const requiredError = validateRequired(confirmPassword, '비밀번호 확인');
    if (requiredError) return requiredError;

    if (password !== confirmPassword) {
        return '비밀번호가 일치하지 않습니다.';
    }
    return '';
};

/**
 * 휴대폰번호 유효성 검증
 * @param phoneNum - 검증할 휴대폰번호
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validatePhoneNumber = (phoneNum: string): string => {
    const requiredError = validateRequired(phoneNum, '휴대폰번호');
    if (requiredError) return requiredError;

    if (!PHONE_REGEX.test(phoneNum)) {
        return PHONE_ERROR_MESSAGE;
    }
    return '';
};

/**
 * 이메일 유효성 검증
 * @param emailInfo - 검증할 이메일
 * @param required - 필수 입력 여부 (기본값: false)
 * @returns 에러 메시지 또는 빈 문자열
 */
export const validateEmail = (emailInfo: string, required = false): string => {
    // 빈 값이고 필수가 아닌 경우 유효
    if (!emailInfo || emailInfo.trim().length === 0) {
        return required ? '이메일을 입력해주세요.' : '';
    }
    // 값이 있으면 형식 검증
    if (!EMAIL_REGEX.test(emailInfo)) {
        return EMAIL_ERROR_MESSAGE;
    }
    return '';
};

/**
 * 금액 유효성 검증 (숫자만, 0 불가)
 * @param amount - 검증할 금액 문자열
 * @returns 유효하면 true, 아니면 false
 */
export const validateAmount = (amount: string): boolean => {
    const numAmount = parseInt(amount, 10);
    return !isNaN(numAmount) && numAmount > 0;
};

// ============================================================
// 복합 검증 함수
// ============================================================

/**
 * 여러 필드를 한번에 검증
 * @param fields - 검증할 필드 배열 (name과 validator 함수 포함)
 * @returns ValidationErrors 객체
 */
export const validateFields = (fields: FieldValidator[]): ValidationErrors => {
    const errors: ValidationErrors = {};

    for (const field of fields) {
        const error = field.validator();
        if (error) {
            errors[field.name] = error;
        }
    }

    return errors;
};

/**
 * ValidationErrors 객체가 비어있는지 확인
 * @param errors - ValidationErrors 객체
 * @returns 오류가 없으면 true
 */
export const hasNoErrors = (errors: ValidationErrors): boolean => {
    return Object.keys(errors).length === 0;
};

/**
 * 폼 전체 유효성 검증 실행
 * @param fields - 검증할 필드 배열
 * @returns {{ isValid: boolean; errors: ValidationErrors }}
 */
export const validateForm = (fields: FieldValidator[]): { isValid: boolean; errors: ValidationErrors } => {
    const errors = validateFields(fields);
    return {
        isValid: hasNoErrors(errors),
        errors,
    };
};

// ============================================================
// 파일 검증 함수
// ============================================================

/** 지원하는 엑셀 파일 확장자 */
const EXCEL_EXTENSIONS = ['.xlsx', '.xls'] as const;

/**
 * 파일이 엑셀 형식인지 검증
 * @param fileName - 검증할 파일명
 * @returns 엑셀 파일 여부
 */
export const isExcelFile = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return EXCEL_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};
